import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as classNames from 'classnames';
import * as React from 'react';
import injectSheet from 'react-jss';
import { ISnackbarClasses, ISnackbarProps, SnackbarVariant } from '.';
import { SnackbarClasses } from './Snackbar.jss';
const AUTO_HIDE_DURATION = 5000;
const EXIT_TRANSITION_DURATION = 200;
const iconForVariant = (variant: SnackbarVariant): IconDefinition => {
  switch (variant) {
    case 'success':
      return faCheckCircle;
    case 'warning':
      return faExclamationTriangle;
    case 'error':
      return faExclamationCircle;
    case 'info':
    default:
      return faInfoCircle;
  }
};
const reducedMotionEnabled = (): boolean => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const UnstyledSnackbar = (props: ISnackbarProps) => {
  const {
    snackbar,
    onClose,
    onExited
  } = props;
  const classes = props.classes as ISnackbarClasses<string>;
  const [isOpen, setIsOpen] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);
  const [hasFocus, setHasFocus] = React.useState(false);
  const remainingDuration = React.useRef(AUTO_HIDE_DURATION);
  React.useEffect(() => {
    const frame = window.requestAnimationFrame(() => setIsOpen(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);
  React.useEffect(() => {
    if (!snackbar.exiting) {
      return;
    }
    const duration = reducedMotionEnabled() ? 0 : EXIT_TRANSITION_DURATION;
    const timeout = window.setTimeout(() => onExited(snackbar.key), duration);
    return () => window.clearTimeout(timeout);
  }, [snackbar.exiting, snackbar.key, onExited]);
  React.useEffect(() => {
    if (snackbar.exiting || isHovered || hasFocus) {
      return;
    }
    const startedAt = Date.now();
    const timeout = window.setTimeout(() => onClose(snackbar.key), remainingDuration.current);
    return () => {
      window.clearTimeout(timeout);
      remainingDuration.current = Math.max(0, remainingDuration.current - (Date.now() - startedAt));
    };
  }, [snackbar.exiting, snackbar.key, isHovered, hasFocus, onClose]);
  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget as Node | null;
    if (!nextTarget || !event.currentTarget.contains(nextTarget)) {
      setHasFocus(false);
    }
  };
  const variantClass = classes[snackbar.variant];
  return <div className={classNames(classes.snackbar, variantClass, isOpen && !snackbar.exiting && classes.open, snackbar.exiting && classes.closing)} role="alert" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} onFocus={() => setHasFocus(true)} onBlur={handleBlur} data-sentry-component="UnstyledSnackbar" data-sentry-source-file="Snackbar.tsx">
            <FontAwesomeIcon className={classes.icon} icon={iconForVariant(snackbar.variant)} fixedWidth aria-hidden="true" data-sentry-element="FontAwesomeIcon" data-sentry-source-file="Snackbar.tsx" />
            <div className={classes.message}>
                {snackbar.message}
            </div>
            <button className={classes.closeButton} type="button" aria-label="Close notification" onClick={() => onClose(snackbar.key)}>
                <FontAwesomeIcon icon={faTimes} fixedWidth aria-hidden="true" data-sentry-element="FontAwesomeIcon" data-sentry-source-file="Snackbar.tsx" />
            </button>
        </div>;
};
const Snackbar = injectSheet(SnackbarClasses)(UnstyledSnackbar);
export default Snackbar;